import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ChainedBackend from 'i18next-chained-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';
import HttpBackend from 'i18next-http-backend';

const BUNDLED_TRANSLATIONS_URL = `${window.location.origin}/lang`;
const PUBLISHED_TRANSLATIONS_URL = process.env.REACT_APP_TRANSLATIONS_URL?.replace(/\/$/, '');
const LANGUAGE_CHANGE_EVENT = 'app-language-changed';

const SUPPORTED_LNGS = [
  'en', 'es', 'pt', 'tr', 'cs', 'sk', 'pl', 'ro', 'no', 'se',
  'de-DE', 'de-AT', 'de-CH',
  'fr-FR', 'fr-CH', 'fr-BE', 'fr-LU',
  'nl-NL', 'nl-BE',
  'it-IT', 'it-CH'
];

const normalizeLng = (lng) => {
  if (!lng) return 'en';

  const cleanLng = String(lng).trim().replace('_', '-');

  if (SUPPORTED_LNGS.includes(cleanLng)) {
    return cleanLng;
  }

  const exactCaseInsensitive = SUPPORTED_LNGS.find(
    (supported) => supported.toLowerCase() === cleanLng.toLowerCase()
  );

  if (exactCaseInsensitive) {
    return exactCaseInsensitive;
  }

  const base = cleanLng.split('-')[0].toLowerCase();

  const baseMatch = SUPPORTED_LNGS.find(
    (supported) =>
      !supported.includes('-') && supported.toLowerCase() === base
  );

  if (baseMatch) {
    return baseMatch;
  }

  const regionalMatch = SUPPORTED_LNGS.find(
    (supported) => supported.split('-')[0].toLowerCase() === base
  );

  if (regionalMatch) {
    return regionalMatch;
  }

  return 'en';
};

const getStoredLoggedUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('loggedUser') || 'null');
  } catch (error) {
    return null;
  }
};

const getPreferredLanguage = () => {
  const loggedUser = getStoredLoggedUser();

  return normalizeLng(
    loggedUser?.preferred_language ||
    loggedUser?.preferredLanguage
  );
};

const initialLng = getPreferredLanguage();

const getTranslationLoadPath = (baseUrl) => (lngs) => {
  const lng = normalizeLng(Array.isArray(lngs) ? lngs[0] : lngs);
  return `${baseUrl}/${lng}.json?ts=${Date.now()}`;
};

const httpBackendOptions = (baseUrl) => ({
  loadPath: getTranslationLoadPath(baseUrl),
  crossDomain: true,
  requestOptions: { cache: 'no-cache' }
});

const getTranslationBackendConfig = () => {
  if (PUBLISHED_TRANSLATIONS_URL) {
    return {
      // Published dictionaries can change without a frontend deployment.
      // Use the bundled dictionary only when a locale is not published yet.
      backends: [HttpBackend, HttpBackend],
      backendOptions: [
        httpBackendOptions(PUBLISHED_TRANSLATIONS_URL),
        httpBackendOptions(BUNDLED_TRANSLATIONS_URL)
      ]
    };
  }

  return {
    backends: [LocalStorageBackend, HttpBackend],
    backendOptions: [
      {
        prefix: 'i18n_res_',
        expirationTime: 60 * 60 * 1000,
        versions: Object.fromEntries(
          SUPPORTED_LNGS.map((language) => [language, 'translations-v2'])
        )
      },
      httpBackendOptions(BUNDLED_TRANSLATIONS_URL)
    ]
  };
};

if (!i18n.isInitialized) {
  i18n
    .use(ChainedBackend)
    .use(initReactI18next)
    .init({
      lng: initialLng,
      fallbackLng: 'en',
      supportedLngs: SUPPORTED_LNGS,
      nonExplicitSupportedLngs: false,
      load: 'currentOnly',
      cleanCode: false,
      lowerCaseLng: false,
      ns: ['translation'],
      defaultNS: 'translation',
      react: {
        useSuspense: false,
        transKeepBasicHtmlNodesFor: ['br', 'strong'],
      },
      interpolation: { escapeValue: false },
      debug: false,
      returnNull: false,
      returnEmptyString: false,
      backend: getTranslationBackendConfig()
    });
}

const applyLanguage = async (lng) => {
  const normalizedLng = normalizeLng(lng);

  document.documentElement.lang = normalizedLng;

  if (i18n.resolvedLanguage !== normalizedLng) {
    await i18n.changeLanguage(normalizedLng);
  }

  window.dispatchEvent(
    new CustomEvent(LANGUAGE_CHANGE_EVENT, {
      detail: { language: normalizedLng }
    })
  );

  return normalizedLng;
};

export default i18n;
export {
  SUPPORTED_LNGS,
  normalizeLng,
  getPreferredLanguage,
  applyLanguage,
  LANGUAGE_CHANGE_EVENT
};